import React, { useContext } from "react";

import { Box } from "@material-ui/core";
// types, constants
import clsx from "clsx";
import {
  BannerSeverity,
  Icon,
  IconName,
  NotificationBanner,
  PolicyDetailsModal,
  PolicyModalButton,
} from "halifax";
import H from "history";
import {
  IIdLodgings,
  IResult,
  ModalScreens,
  POLICY_MODAL,
  RecentHotelSearch,
  VIEWED_POLICY_MODAL,
} from "redmond";
import { HotelSearchControlConnectorProps } from "./container";
import * as textConstants from "./textConstants";

// styles, components
import { CalendarPickerButton } from "./components/CalendarPickerButton";
import { LocationAutocomplete } from "./components/LocationAutocomplete";
import { OccupancySelection } from "./components/OccupancySelection";
import { HotelSearchButton } from "./components/SearchButton";
import "./styles.scss";

// helpers, utils
import { usePrevious } from "../../../../hooks/usePrevious";
import {
  PATH_AVAILABILITY,
  PATH_STAYS_AVAILABILITY,
} from "../../../../utils/paths";
import { transformToStringifiedAvailabilityQuery } from "../../../shop/utils/queryStringHelpers";

// experiments
import { STAYS_WISHLIST } from "@capone/experiments";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CONTROL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  HOTEL_COLOR_CALENDAR,
  HOTEL_COLOR_CALENDAR_VARIANTS,
  HOTEL_COLOR_CALENDAR_WITH_PRICING,
  STAYS_HOMEPAGE,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { WishlistSearchEntry } from "../../../wishlist/components/WishlistSearchEntry/component";

export interface IHotelSearchControlProps
  extends HotelSearchControlConnectorProps {
  recentSearches?: RecentHotelSearch[];
  onRecentSearchClick?: (search: RecentHotelSearch) => void;
  showPreferencesBanner?: boolean;
}

export const HotelSearchControl = (props: IHotelSearchControlProps) => {
  const {
    className,
    onSearch,
    displaySearchOnChange = false,
    location,
    fromDate,
    untilDate,
    adultsCount,
    children,
    showTravelerSelection = true,
    showSearchButton = true,
    saveDatesOnClose,
    resetFilters,
    roomsCount,
    petsCount,
    hideCalendarSeparator = false,
    showTotalTravelers,
    recentSearches,
    onRecentSearchClick,
    isViewHotelsNear,
    isSearchMap,
  } = props;
  const [hasDatesChanged, setHasDatesChanged] = React.useState<boolean>(false);
  const [hasLocationChanged, setHasLocationChanged] =
    React.useState<boolean>(false);
  const [hasMissingSearchInfoError, setHasMissingSearchInfoError] =
    React.useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = React.useState(false);

  const { policies } = useContext(ClientContext);

  const expState = useExperiments();

  const isFirstUpdate = React.useRef<boolean>(true);
  const prevFrom = usePrevious(fromDate);
  const prevUntil = usePrevious(untilDate);
  const prevLocation = usePrevious(location);

  const getSelectedOption = (option: IResult | null, value: IResult | null) => {
    const selection = value ? (value.id as IIdLodgings) : null;
    const opt = option ? (option.id as IIdLodgings) : null;
    return (
      !!selection &&
      !!opt &&
      selection.lodgingSelection.placeId === opt.lodgingSelection.placeId
    );
  };

  const colorCalendarExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    HOTEL_COLOR_CALENDAR,
    HOTEL_COLOR_CALENDAR_VARIANTS
  );

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const searchButtonMessage = () =>
    staysHomepageEnabled
      ? textConstants.SEARCH_STAYS
      : textConstants.SEARCH_HOTELS;

  React.useEffect(() => {
    if (location && fromDate && untilDate) {
      const hasChangedDates = fromDate !== prevFrom && untilDate !== prevUntil;
      const hasChangedLocation =
        (prevLocation?.id as IIdLodgings)?.lodgingSelection?.placeId !==
        (location?.id as IIdLodgings)?.lodgingSelection?.placeId;
      // skip the first update
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
      } else if (hasChangedDates) {
        setHasDatesChanged(true);
      } else if (hasChangedLocation) {
        setHasLocationChanged(true);
      }
    }
  }, [location, fromDate, untilDate, adultsCount, children]);

  React.useEffect(() => {
    setHasLocationChanged(false);
  }, [isViewHotelsNear, isSearchMap]);

  const isReadyToSearch = !!location && !!fromDate && !!adultsCount;

  const handleSearch = (history: H.History) => {
    const path = staysHomepageEnabled
      ? PATH_STAYS_AVAILABILITY
      : PATH_AVAILABILITY;

    history.push(
      `${path}${transformToStringifiedAvailabilityQuery(
        (location?.id as IIdLodgings).lodgingSelection.searchTerm,
        fromDate,
        untilDate,
        adultsCount,
        children,
        roomsCount,
        petsCount
      )}`
    );

    onSearch && onSearch(history, false);
    setHasDatesChanged(false);
    setHasLocationChanged(false);
    resetFilters();
  };
  const handleSearchClick = (history: H.History) => {
    isReadyToSearch
      ? handleSearch(history)
      : setHasMissingSearchInfoError(true);
  };

  React.useEffect(() => {
    if (isReadyToSearch) {
      setHasMissingSearchInfoError(false);
    }
  }, [fromDate, untilDate, adultsCount]);

  const onShowPolicyDetailsModal = () => {
    setIsPolicyModalOpen(true);
    trackEvent({
      eventName: VIEWED_POLICY_MODAL,
      properties: {
        type: POLICY_MODAL,
        entry_point: ModalScreens.HOTELS_SEARCH,
        funnel: "hotels",
      },
    });
  };

  return (
    <Box className={clsx("hotel-search-control-root", className, "multiroom")}>
      <Box className="hotel-search-control-row">
        <Box className="hotel-search-inputs">
          {showSearchButton && (
            <>
              <PolicyModalButton
                policies={policies}
                onClick={onShowPolicyDetailsModal}
              />
              <PolicyDetailsModal
                policies={policies}
                isOpen={isPolicyModalOpen}
                setIsOpen={setIsPolicyModalOpen}
                productType="hotel"
              />
            </>
          )}
          <Box className="hotel-search-input-row">
            <LocationAutocomplete
              className={clsx("destination-auto-complete", "b2b")}
              label={textConstants.LOCATION_AUTOCOMPLETE_PLACEHOLDER}
              getOptionSelected={getSelectedOption}
              recentlySearchedTopContent={
                isWishlistExperiment ? (
                  <WishlistSearchEntry
                    width="100%"
                    height="45px"
                    paddingX="20px"
                    paddingY="10px"
                  />
                ) : undefined
              }
              customIcon={
                <Icon name={IconName.B2BMapPin} ariaLabel="" aria-hidden />
              }
              hasMissingSearchInfoError={hasMissingSearchInfoError && !location}
              recentSearches={recentSearches}
              onRecentSearchClick={
                onRecentSearchClick
                  ? (recentSearch) =>
                      onRecentSearchClick(recentSearch as RecentHotelSearch)
                  : undefined
              }
            />
            <Box className="date-input">
              <CalendarPickerButton
                saveDatesOnClose={saveDatesOnClose}
                hasMissingSearchInfoError={
                  hasMissingSearchInfoError && !fromDate && !untilDate
                }
                hideSeparator={hideCalendarSeparator}
                showCalenderPricingColors={
                  colorCalendarExperimentVariant !== CONTROL
                }
                showCalendarPricing={
                  colorCalendarExperimentVariant ===
                  HOTEL_COLOR_CALENDAR_WITH_PRICING
                }
              />
            </Box>
            {showTravelerSelection && (
              <div className="occupancy-selection">
                <OccupancySelection
                  showTotalTravelers={showTotalTravelers}
                  showPets={staysHomepageEnabled}
                />
              </div>
            )}
          </Box>
        </Box>
        {showSearchButton &&
          (!displaySearchOnChange || hasLocationChanged || hasDatesChanged) && (
            <HotelSearchButton
              className={clsx("hotel-search-control-button", "b2b", {
                xmod: isXmodRedesign,
              })}
              message={searchButtonMessage()}
              onClick={(history) => {
                handleSearchClick(history);
              }}
              enabled
            />
          )}
      </Box>
      {hasMissingSearchInfoError && (
        <Box className="missing-info-search-error-container">
          <NotificationBanner
            className={clsx("missing-info-search-error-banner")}
            label={textConstants.MISSING_INFO_SEARCH_ERROR}
            severity={BannerSeverity.ERROR}
            icon={<Icon name={IconName.WarningAlert} />}
          />
        </Box>
      )}
    </Box>
  );
};
